/**
 * Single source of truth for the GL-review reasons that put a transaction on
 * /review-queue (i.e. it needs a human GL decision).
 *
 * Previously this set was hand-duplicated in 4 places (dashboardCharts,
 * transactions.listReviewQueue, transactionStage, Transactions.tsx) and drifted,
 * which let flagged rows silently vanish from the review queue. Everything now
 * imports from here.
 */
export const REVIEW_REASONS = [
  "vendor_unknown",
  "card_mismatch",
  "multiple_no_gl",
  "invoice_unreadable",
  "missing_gl_account",
  "ocr_exception",
  "review_queue_policy",
  // A matched row whose GL came from a low-confidence categorization (< 0.95).
  // The agent-3 posting gate (R4) holds these for human account-confirmation
  // instead of auto-posting a shaky fuzzy GL. Anchored on the reason so the row
  // surfaces on /review-queue (previously the reason lived only in the audit log,
  // so these rows were invisible AND churned by the self-healing sweep).
  "low_confidence_gl",
] as const;

export type ReviewReason = (typeof REVIEW_REASONS)[number];

export const REVIEW_REASONS_SET: ReadonlySet<string> = new Set(REVIEW_REASONS);

/** True when a reason string is a GL-review reason (needs a human on /review-queue). */
export function isReviewReason(reason: string | null | undefined): reason is ReviewReason {
  return typeof reason === "string" && REVIEW_REASONS_SET.has(reason);
}

/**
 * The canonical predicate for "this row belongs on /review-queue": it carries a
 * GL-review reason and has not yet been posted to QuickBooks. Anchored on the
 * flag/reason — NOT on postingStatus — so a flagged row can never silently fall
 * off the queue just because some writer left postingStatus at "pending"/"categorized".
 */
export function needsGlReview(t: {
  vendorGlReviewReason?: string | null;
  categorizationFlagged?: boolean;
  postingStatus?: string;
  qbTransactionId?: string | null;
  matchStatus?: string | null;
  invoiceRequired?: boolean;
}): boolean {
  if (t.postingStatus === "posted") return false;
  if (t.qbTransactionId) return false;
  if (!isReviewReason(t.vendorGlReviewReason)) return false;
  // Client rule (2026-06-18, Michelle): a GL decision is made WITH the
  // supporting invoice in hand. A row that has NO invoice attached yet must
  // NOT surface in the Review Queue — it waits in "Needs Invoice" until an
  // invoice is matched (or proposed). It only graduates to the Review Queue
  // once an invoice exists. Accounts that don't require an invoice
  // (invoiceRequired === false, Michelle's per-account rule) are exempt and
  // review immediately, since they post without a match by design.
  if (t.invoiceRequired === false) return true;
  return t.matchStatus === "matched" || t.matchStatus === "human_review";
}
